import React, { FC, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { makeStyles, Box, TextField } from "@material-ui/core";
import { Loading } from "../components/Loading";
import { ForgotPassword } from "../components/ForgotPassword";
import { Text } from "../components/Text";
import Button from "../components/Button";
import { store } from "../store";
import initNetlifyIdentity from "../config/InitNetlifyIdentity";
import { useIdentityContext } from "react-netlify-identity";
import { withRouter, RouteComponentProps } from "../utils/useRouter";

const Login: FC<RouteComponentProps> = observer(({ history }) => {
  const { loginUser } = useIdentityContext();
  const {
    inputEmail,
    password,
    isEmailValid,
    errors,
    clearLocalDesigns,
    getLocalDesigns,
    setErrors,
  } = store;
  let { appLoading } = store;
  const [forgotPassword, setForgotPassword] = useState<boolean>(false);
  const classes = styles();

  useEffect(() => {
    initNetlifyIdentity();
  }, []);

  const handleLogin = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    let allErrors = [];
    if (!isEmailValid) allErrors.push("Please enter a valid email address");
    if (password.length < 6)
      allErrors.push("Your password must be more than 6 characters");
    setErrors(allErrors);
    if (!allErrors.length) {
      login();
    }
    return false;
  };

  const login = async () => {
    appLoading = true;
    try {
      const user = await loginUser(inputEmail, password);
      // associate designs with user
      const designs = getLocalDesigns();
      if (designs?.length) {
        try {
          await fetch(`/.netlify/functions/PostSignIn`, {
            method: "post",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: user ? `Bearer ${await user?.jwt()}` : null,
            },
            body: JSON.stringify({
              email: inputEmail,
              insertedIds: designs.map(({ result }) =>
                typeof result === "string" ? JSON.parse(result)._id : result._id
              ),
            }),
          });
        } catch (e) {
          console.log(e);
        }
      }
      appLoading = false;
      history.push("/");
      clearLocalDesigns();
      setErrors([]);
    } catch (err) {
      appLoading = false;
      if (err?.message?.includes("Email not confirmed")) {
        return setErrors([
          "Please confirm your email address using the link we sent you",
        ]);
      }
      setErrors(["Incorrect email and/or password"]);
      console.log(err?.message);
    }
  };

  if (appLoading) return <Loading text="Loading..." />;
  if (forgotPassword)
    return <ForgotPassword setForgotPassword={setForgotPassword} />;

  return (
    <Box className={classes.container}>
      <form onSubmit={handleLogin} className={classes.innerContainer}>
        <Box className={classes.inputContainer}>
          <TextField
            InputProps={{ classes: { input: classes.input } }}
            placeholder="EMAIL"
            className={classes.input}
            type="email"
            value={inputEmail}
            error={!!errors.find((err) => err.includes("email"))}
            name="inputEmail"
            variant="filled"
            onChange={(e) =>
              (store[e.currentTarget.name] = e.currentTarget.value)
            }
          />
        </Box>
        <Box className={classes.inputContainer}>
          <TextField
            InputProps={{ classes: { input: classes.input } }}
            placeholder="PASSWORD"
            className={classes.input}
            type="password"
            value={password}
            name="password"
            variant="filled"
            error={!!errors.find((err) => err.includes("password"))}
            onChange={(e) =>
              (store[e.currentTarget.name] = e.currentTarget.value)
            }
          />
        </Box>
        <Box className={classes.errors}>
          {errors.map((error) => (
            <Text size="small" color="error" key={error}>
              {error}
            </Text>
          ))}
        </Box>
        <Button type="submit">SIGN IN</Button>
        <Box
          className={classes.contextualLink}
          onClick={() => setForgotPassword(true)}
        >
          Forgot your password?
        </Box>
      </form>
    </Box>
  );
});

const styles = makeStyles((theme) => ({
  container: {
    padding: 20,
    [theme.breakpoints.up("md")]: {
      borderRadius: 0,
      boxShadow: "0 50vh 0 50vh #F2F2F2",
      padding: 50,
      width: "100%",
    },
  },
  innerContainer: {
    [theme.breakpoints.up("md")]: {
      maxWidth: 600,
      margin: "0 auto",
    },
  },
  input: {
    width: "100%",
    background: "white",
  },
  inputContainer: {
    margin: "20px 0px",
  },
  message: {
    display: "flex",
    justifyContent: "center",
  },
  errors: {
    marginBottom: 20,
  },
  contextualLink: {
    marginTop: 20,
    cursor: "pointer",
    textAlign: "center",
  },
}));

export default withRouter(Login);
